import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BriefcaseIcon, Building2Icon, CheckIcon, ClockIcon, GraduationCapIcon, HandHeartIcon, LockIcon, MailIcon, MapPinIcon, PhoneIcon, ShieldCheckIcon, BoxIcon } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Container } from "../components/ui/Container";
import { GeometricAccent } from "../components/ui/GeometricAccent";
import { MosqueArchMask } from "../components/ui/MosqueArchMask";
import { ObfuscatedEmail } from "../components/ui/ObfuscatedEmail";
import { PatternDecor } from "../components/ui/PatternDecor";
type ContactRoute = 'general' | 'teacher' | 'volunteers' | 'careers';
type ContactFormData = {
  name: string;
  email: string;
  country: string;
  role: string;
  message: string;
};
type FormErrors = Partial<Record<keyof ContactFormData | 'consent', string>>;
type RouteDetail = {
  label: string;
  description: string;
  icon: BoxIcon;
  eyebrow: string;
  title: string;
  intro?: string;
  firstHeading: string;
  firstList: string[];
  secondHeading: string;
  secondList: string[];
  footer?: string;
};
const emptyForm: ContactFormData = {
  name: '',
  email: '',
  country: '',
  role: '',
  message: ''
};
const WEB3FORMS_ACCESS_KEY = 'e3cb5ab6-527c-4e33-a0c2-8cb9b536889b';
const routeDetails: Record<ContactRoute, RouteDetail> = {
  general: {
    label: 'استفسار عام',
    description: 'أسئلة أو معلومات أو شراكات — نرد خلال 48 ساعة.',
    icon: MailIcon,
    eyebrow: 'تواصل معنا',
    title: 'يسعدنا التواصل معك',
    firstHeading: 'نرحب باستفسارات:',
    firstList: ['الآباء والأمهات والأسر', 'المعلّمين والمهنيين', 'الشركاء والمؤسسات', 'المانحين والمؤسسات الداعمة'],
    secondHeading: 'تواصل معنا بشأن:',
    secondList: ['الأسئلة العامة', 'مناقشات الشراكة', 'معلومات البرامج', 'المنح الدراسية'],
    footer: 'تُعامل جميع الرسائل بعناية وسرّية.'
  },
  teacher: {
    label: 'كن معلّمًا',
    description: 'مسارات التدريس والإرشاد والتطوير للمعلّمين.',
    icon: GraduationCapIcon,
    eyebrow: 'كن معلّمًا',
    title: 'انضم إلى رسالتنا التعليمية',
    intro: 'نتعاون مع معلّمين وخبراء مؤهلين يشاركوننا التزامنا بالتعليم الأخلاقي المراعي للثقافة.',
    firstHeading: 'يسهم المعلّمون في المركز الدولي للتعلّم الإسلامي في:',
    firstList: ['برامج تعليم الأطفال والبالغين', 'أنماط التعلّم عبر الإنترنت والتعلّم المدمج', 'بيئات تعليمية شاملة وقائمة على القيم'],
    secondHeading: 'نرحب بالمهنيين ذوي الخبرة في:',
    secondList: ['التعليم وطرائق التدريس', 'اللغات والدراسات الثقافية', 'تعليم الأسرة والمجتمع'],
    footer: 'تتوافق جميع أدوار التدريس مع معايير الحماية والأخلاقيات والجودة لدينا.'
  },
  volunteers: {
    label: 'المتطوعون',
    description: 'ادعم برامجنا بصفتك متطوعًا في المجتمع.',
    icon: HandHeartIcon,
    eyebrow: 'المتطوعون',
    title: 'ادعم الأسر. قوِّ المجتمعات.',
    intro: 'يلعب المتطوعون دورًا مهمًا في دعم مبادراتنا التي تركز على المجتمع.',
    firstHeading: 'قد تشمل فرص التطوع:',
    firstList: ['دعم المجتمع والتنسيق', 'المساعدة التعليمية', 'الترجمة ودعم المحتوى', 'التواصل والاتصال'],
    secondHeading: 'التطوع معنا فرصة من أجل:',
    secondList: ['الإسهام بصورة هادفة', 'دعم الأسر المسلمة التي تعيش كأقليات', 'اكتساب خبرة في بيئة دولية تقودها رسالة واضحة']
  },
  careers: {
    label: 'الوظائف',
    description: 'فرص للمعلّمين والمصممين والمختصين التشغيليين.',
    icon: BriefcaseIcon,
    eyebrow: 'الوظائف',
    title: 'أدوار مهنية ذات هدف',
    intro: 'نقدم فرصًا مهنية لمن يبحث عن عمل هادف يرتبط بالتعليم والأثر الاجتماعي والمسؤولية الأخلاقية.',
    firstHeading: 'قد تشمل الفرص المهنية أدوارًا في:',
    firstList: ['تنسيق البرامج', 'التقديم التعليمي', 'الإدارة والعمليات', 'الشراكات وإدارة المشاريع'],
    secondHeading: 'نحن نُقدّر:',
    secondList: ['الاحترافية', 'الوعي الثقافي', 'النزاهة والمسؤولية'],
    footer: 'قد تكون الوظائف بدوام كامل أو جزئي أو على أساس المشاريع، بحسب احتياجات البرامج والتمويل.'
  }
};
const hashToRoute: Record<string, ContactRoute> = {
  '#general-inquiry': 'general',
  '#become-a-teacher': 'teacher',
  '#volunteers': 'volunteers',
  '#careers': 'careers'
};
export function ArabicContact() {
  const location = useLocation();
  const [route, setRoute] = useState<ContactRoute>('general');
  const [formData, setFormData] = useState<ContactFormData>(emptyForm);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const honeypotRef = useRef<HTMLInputElement>(null);
  const detail = routeDetails[route];
  useEffect(() => {
    const matchedRoute = hashToRoute[location.hash];
    if (!matchedRoute) return;
    setRoute(matchedRoute);
    window.setTimeout(() => document.getElementById('contact-routes')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    }), 100);
  }, [location.hash]);
  function updateField(field: keyof ContactFormData, value: string) {
    setFormData(previous => ({
      ...previous,
      [field]: value
    }));
    setErrors(previous => {
      if (!previous[field]) return previous;
      const next = {
        ...previous
      };
      delete next[field];
      return next;
    });
  }
  function validate(): FormErrors {
    const next: FormErrors = {};
    if (!formData.name.trim()) next.name = 'يرجى إدخال الاسم.';
    if (!formData.email.trim()) next.email = 'يرجى إدخال البريد الإلكتروني.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) next.email = 'يرجى إدخال بريد إلكتروني صالح.';
    if (!formData.message.trim()) next.message = 'يرجى كتابة رسالتك.';
    if (!consent) next.consent = 'يرجى تأكيد موافقتك قبل الإرسال.';
    return next;
  }
  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSubmitStatus('idle');
      return;
    }
    if (honeypotRef.current?.value) {
      setSubmitStatus('success');
      setFormData(emptyForm);
      setConsent(false);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `رسالة ${detail.label} جديدة من ${formData.name.trim()}`,
          from_name: 'IILH Website — Arabic Contact Form',
          inquiry_type: detail.label,
          name: formData.name.trim(),
          email: formData.email.trim(),
          country: formData.country.trim(),
          role: formData.role.trim(),
          message: formData.message.trim(),
          consent_given: 'نعم — وافق على سياسة الخصوصية'
        })
      });
      const result: {
        success?: boolean;
      } = await response.json();
      if (!result.success) throw new Error('Submission failed');
      setSubmitStatus('success');
      setFormData(emptyForm);
      setConsent(false);
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-2490" data-mp="1vo6cpdvpt2yx">
      <section className="relative isolate overflow-hidden" data-id="element-2491" data-mp="20loord513yb5">
        <PatternDecor side="right" y="middle" size="lg" data-id="element-2492" data-mp="20o2i9hk292gl" />
        <GeometricAccent variant="dots" className="absolute left-6 top-10 h-32 w-32" opacity={0.4} data-id="element-2493" data-mp="1mvcrz0xi1bdo" />
        <Container className="relative py-16 md:py-24" data-id="element-2494" data-mp="1qv3gsz3gthfk">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-2495" data-mp="1qtshbbh1yvu9">
            <div className="lg:col-span-6" data-id="element-2496" data-mp="2ejmphfqffw86"><span className="inline-flex rounded-full bg-sage-100 px-3 py-1.5 text-[11px] font-semibold text-forest-700" data-id="element-2497" data-mp="2jc7gyalv1x8j">تواصل معنا</span><h1 className="mt-5 font-display text-4xl font-semibold leading-[1.45] text-ink-900 md:text-6xl" data-id="element-2498" data-mp="1um1q89xqsp0z">يسعدنا التواصل معك</h1><p className="mt-5 max-w-xl text-base leading-loose text-ink-700 md:text-lg" data-id="element-2499" data-mp="1pubf41qzdx8i">سواء كنت أحد الوالدين أو شريكًا أو معلّمًا أو داعمًا، فنحن هنا للاستماع إليك. اختر المسار المناسب وسيرد فريقنا بسرعة.</p></div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-2500" data-mp="1fyqg8ch7ld2z"><div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-2501" data-mp="23l6pjih4ome7"><MosqueArchMask src="https://cdn.magicpatterns.com/uploads/ehqbib2hEnjeCwyz1Hvru2/Contact7..jpg" alt="دفتر يحمل هوية المركز الدولي للتعلّم الإسلامي على مكتب خشبي بجوار كتب وأدوات مكتبية" data-id="element-2502" data-mp="1yo6hj20px9kg" /></div></motion.div>
          </div>
        </Container>
      </section>

      <section id="contact-routes" className="scroll-mt-24 bg-sage-50/60 pb-12" data-id="element-2503" data-mp="2yvlh3xj1rpgq"><Container data-id="element-2504" data-mp="2ytieme0pk3v4"><div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-2505" data-mp="2ytqrw70qtb6u">{(Object.entries(routeDetails) as Array<[ContactRoute, RouteDetail]>).map(([key, item], index) => <RouteCard key={key} item={item} active={route === key} index={index} onSelect={() => {
            setRoute(key);
            setSubmitStatus('idle');
          }} data-id="element-2506" data-mp="38sq23u15pevy" data-mpi={index} />)}</div></Container></section>

      <section className="relative isolate overflow-hidden bg-white/60 py-12 md:py-16" data-id="element-2507" data-mp="3iij5w8v24ppn"><PatternDecor side="left" y="bottom" size="md" data-id="element-2508" data-mp="3mkeelyfbi5dg" /><Container data-id="element-2509" data-mp="3mjctt28i24ij"><div className="grid items-start gap-12 lg:grid-cols-12" data-id="element-2510" data-mp="3mn0ldvudwgum"><div className="lg:col-span-6" data-id="element-2511" data-mp="3rlaiibzo5riv"><AnimatePresence mode="wait" data-id="element-2512" data-mp="3rl26snfa8s99"><RouteDescription key={route} detail={detail} data-id="element-2513" data-mp="dxvvhg0olvk" /></AnimatePresence></div><div className="lg:col-span-6" data-id="element-2514" data-mp="d6bl2rveakl"><ContactForm routeLabel={detail.label} data={formData} errors={errors} consent={consent} status={submitStatus} isSubmitting={isSubmitting} honeypotRef={honeypotRef} onChange={updateField} onConsentChange={setConsent} onSubmit={handleSubmit} data-id="element-2515" data-mp="1x6hmwcysd0qx" /></div></div></Container></section>

      <section className="bg-[#EDF2F7]/60 py-12 md:py-16" data-id="element-2516" data-mp="ck30s0twloqy"><Container data-id="element-2517" data-mp="hhw32is35yly"><div className="grid gap-5 md:grid-cols-3" data-id="element-2518" data-mp="hhmft2yfe5jy"><InfoCard icon={ShieldCheckIcon} title="السرّية أولًا" tone="bg-sage-50 border-sage-200" data-id="element-2519" data-mp="mfxmx5xc61yh">كل التواصلات خاصة. نتبع معايير صارمة للحماية، وخصوصًا في الاستفسارات التي تتعلق بالأطفال أو المدارس أو التمييز.</InfoCard><InfoCard icon={ClockIcon} title="وقت الاستجابة" tone="bg-sand-50 border-sand-200" data-id="element-2520" data-mp="vgvs3rjigfin">تُراجع معظم الرسائل خلال 24–48 ساعة، من الإثنين إلى الجمعة. وتحظى الحالات الحساسة بالأولوية.</InfoCard><InfoCard icon={Building2Icon} title="المقر الرئيسي" tone="bg-white border-sage-200" data-id="element-2521" data-mp="10dcjcum0b9zy"><ul className="mt-3 space-y-3 text-sm leading-loose text-ink-700" data-id="element-2522" data-mp="158icaddttoxj"><li className="flex items-start gap-3" data-id="element-2523" data-mp="1a4gxjs3uyqtq"><MapPinIcon size={16} className="mt-1 shrink-0 text-forest-700" aria-hidden="true" data-id="element-2524" data-mp="1a7l9xcrh8svh" /><span dir="ltr" data-id="element-2525" data-mp="1e8x0bki6di4q">Aia tn 10, Tallinn, 10111 Estonia</span></li><li className="flex items-start gap-3" data-id="element-2526" data-mp="1j5f2qdq194dw"><PhoneIcon size={16} className="mt-1 shrink-0 text-forest-700" aria-hidden="true" data-id="element-2527" data-mp="1j6ge6jaynsw5" /><a href="tel:+37258888275" dir="ltr" className="transition hover:text-forest-700" data-id="element-2528" data-mp="33p0mjx9ofm2h">+372 5888 8275</a></li><li className="flex items-start gap-3" data-id="element-2529" data-mp="2ynl1w6bevhkm"><MailIcon size={16} className="mt-1 shrink-0 text-forest-700" aria-hidden="true" data-id="element-2530" data-mp="2unj9knrkg0i0" /><ObfuscatedEmail user="info" domain="iilh.org" className="transition hover:text-forest-700" data-id="element-2531" data-mp="2upnd34ljyxhr" /></li></ul></InfoCard></div></Container></section>
    </div>;
}
function RouteCard({
  item,
  active,
  index,
  onSelect
}: {
  item: RouteDetail;
  active: boolean;
  index: number;
  onSelect: () => void;
}) {
  const Icon = item.icon;
  return <motion.button type="button" onClick={onSelect} initial={{
    opacity: 0,
    y: 12
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.4,
    delay: index * 0.06
  }} className={`rounded-2xl border p-5 text-right transition-all ${active ? 'border-forest-700 bg-forest-700 text-ivory shadow-card' : 'border-sage-200 bg-white text-ink-900 hover:border-forest-700/30'}`} data-id="element-2532" data-mp="3dlvhk66qr4c0"><span className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${active ? 'bg-ivory/15 text-ivory' : 'bg-sage-100 text-forest-700'}`} data-id="element-2533" data-mp="o3m0zbv1il2d"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-2534" data-mp="fbbsq03pgpnr" /></span><p className="mt-4 text-sm font-semibold" data-id="element-2535" data-mp="f8pl60gtea5j">{item.label}</p><p className={`mt-2 text-xs leading-loose ${active ? 'text-ivory/70' : 'text-ink-500'}`} data-id="element-2536" data-mp="jaszcwuvbsui">{item.description}</p></motion.button>;
}
function RouteDescription({
  detail
}: {
  detail: RouteDetail;
}) {
  const Icon = detail.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -8
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} data-id="element-2537" data-mp="a9cqwunm60sx"><div className="inline-flex items-center gap-2 text-forest-700" data-id="element-2538" data-mp="fcahwmvt94w"><Icon size={18} strokeWidth={1.8} aria-hidden="true" data-id="element-2539" data-mp="1qqqqh63qnljx" /><span className="text-[11px] font-semibold" data-id="element-2540" data-mp="1urakw1wxgb2d">{detail.eyebrow}</span></div><h2 className="mt-3 font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2541" data-mp="1uqhmbo84grvb">{detail.title}</h2>{detail.intro && <p className="mt-5 leading-loose text-ink-500" data-id="element-2542" data-mp="3b7q5uyvsg3ui">{detail.intro}</p>}<DetailList heading={detail.firstHeading} items={detail.firstList} data-id="element-2543" data-mp="3b9kqsd727vyl" /><DetailList heading={detail.secondHeading} items={detail.secondList} data-id="element-2544" data-mp="3p06au5892by2" />{detail.footer && <p className="mt-7 leading-loose text-ink-500" data-id="element-2545" data-mp="3k864nv1l6mt1">{detail.footer}</p>}</motion.div>;
}
function DetailList({
  heading,
  items
}: {
  heading: string;
  items: string[];
}) {
  return <div data-id="element-2546" data-mp="3tyhkaeumukfr"><p className="mt-7 font-semibold leading-loose text-ink-500" data-id="element-2547" data-mp="3tyhic9b2oad4">{heading}</p><ul className="mt-3 space-y-2.5" data-id="element-2548" data-mp="3u0k8ctc863hu">{items.map(item => <li key={item} className="flex items-start gap-2.5 leading-loose text-ink-700" data-id="element-2549" data-mp="brjoez6prm3c"><CheckIcon size={16} className="mt-1.5 shrink-0 text-forest-700" strokeWidth={2.2} aria-hidden="true" data-id="element-2550" data-mp="6t02uzw18cbi" />{item}</li>)}</ul></div>;
}
function ContactForm({
  routeLabel,
  data,
  errors,
  consent,
  status,
  isSubmitting,
  honeypotRef,
  onChange,
  onConsentChange,
  onSubmit
}: {
  routeLabel: string;
  data: ContactFormData;
  errors: FormErrors;
  consent: boolean;
  status: 'idle' | 'success' | 'error';
  isSubmitting: boolean;
  honeypotRef: React.RefObject<HTMLInputElement | null>;
  onChange: (field: keyof ContactFormData, value: string) => void;
  onConsentChange: (value: boolean) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  return <form noValidate onSubmit={onSubmit} className="rounded-3xl border border-sage-200 bg-white p-7 shadow-soft md:p-10" data-id="element-2551" data-mp="1rbk8w779zteo"><input ref={honeypotRef} type="text" name="botcheck" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-id="element-2552" data-mp="1wa40oo8jvzey" /><p className="mb-1 text-[11px] font-semibold text-forest-700" data-id="element-2553" data-mp="218yflf3ed3ts">{routeLabel}</p><h2 className="font-display text-2xl font-semibold text-ink-900 md:text-3xl" data-id="element-2554" data-mp="260yj1hioeev4">أرسل لنا رسالة</h2><p className="mt-2 text-sm leading-loose text-ink-500" data-id="element-2555" data-mp="18lkt6axbrqnx">تُعامل جميع الحقول بسرّية.</p><div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-2556" data-mp="1djw12g427r3i"><Field label="الاسم الكامل" placeholder="اكتب اسمك الكامل" value={data.name} required error={errors.name} onChange={value => onChange('name', value)} data-id="element-2557" data-mp="1dium40jxakgi" /><Field label="البريد الإلكتروني" placeholder="you@example.com" type="email" value={data.email} required error={errors.email} onChange={value => onChange('email', value)} data-id="element-2558" data-mp="111tc8owphrbn" /><Field label="الدولة" placeholder="الدولة التي تقيم فيها" value={data.country} onChange={value => onChange('country', value)} data-id="element-2559" data-mp="w4jrhmk9i3u5" /><Field label="الصفة أو الدور" placeholder="والد/ة، معلّم/ة، مانح/ة…" value={data.role} onChange={value => onChange('role', value)} data-id="element-2560" data-mp="n1ik7ijnu6ow" /></div><div className="mt-4" data-id="element-2561" data-mp="ia0sll3j1igl"><label htmlFor="arabic-contact-message" className="mb-1.5 block text-sm font-medium text-ink-900" data-id="element-2562" data-mp="i9r3dzqb3fby">الرسالة <span className="text-forest-700" data-id="element-2563" data-mp="dc8rq1kwmm09">*</span></label><textarea id="arabic-contact-message" rows={5} placeholder="شارك ما تحتاج إليه — نقرأ كل رسالة." value={data.message} onChange={event => onChange('message', event.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'arabic-message-error' : undefined} className={`w-full rounded-xl border bg-ivory/40 px-4 py-3 text-[15px] transition focus:bg-white focus:outline-none ${errors.message ? 'border-red-400 focus:border-red-500' : 'border-sage-200 focus:border-forest-700'}`} data-id="element-2564" data-mp="8de9p1urh1h7" />{errors.message && <p id="arabic-message-error" className="mt-1.5 text-xs text-red-600" data-id="element-2565" data-mp="3bx3o4yys2y97">{errors.message}</p>}</div><div aria-live="polite" data-id="element-2566" data-mp="3gvoem6o8a3kg">{status === 'success' && <div className="mt-4 rounded-xl border border-forest-200 bg-forest-50 p-4 text-sm leading-loose text-forest-800" data-id="element-2567" data-mp="8f20phgtvexx">✓ تم إرسال رسالتك بنجاح. سنرد عليك خلال 24 إلى 48 ساعة.</div>}{status === 'error' && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm leading-loose text-red-800" data-id="element-2568" data-mp="cihyih4mvlc7">تعذّر إرسال الرسالة. يُرجى المحاولة مرة أخرى أو مراسلتنا مباشرة على <ObfuscatedEmail user="info" domain="iilh.org" className="underline" data-id="element-2569" data-mp="3minilm1yltn" />.</div>}</div><div className="mt-5" data-id="element-2570" data-mp="rd5zbhz39vlh"><label className="flex cursor-pointer items-start gap-3" data-id="element-2571" data-mp="rcmn8hw4e1fd"><input type="checkbox" checked={consent} onChange={event => onConsentChange(event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-sage-300 accent-forest-700 focus:ring-forest-700/40" data-id="element-2572" data-mp="w4watr9ripeh" /><span className="text-xs leading-loose text-ink-700" data-id="element-2573" data-mp="11inwgxk6gvfg">أوافق على معالجة المركز الدولي للتعلّم الإسلامي للبيانات التي أقدمها للرد على استفساري، وفقًا لـ<Link to="/ar/legal/privacy-policy" className="text-forest-700 underline hover:text-forest-800" data-id="element-2574" data-mp="1fbdpvn21d2ml">سياسة الخصوصية</Link>.</span></label>{errors.consent && <p className="mt-1.5 text-xs text-red-600" data-id="element-2575" data-mp="mln6ayhmx8j3">{errors.consent}</p>}</div><div className="mt-5 flex flex-wrap items-center justify-between gap-4" data-id="element-2576" data-mp="ija1ccappend"><p className="flex items-center gap-2 text-xs leading-loose text-ink-500" data-id="element-2577" data-mp="il3apzcpmcz2"><LockIcon size={12} aria-hidden="true" data-id="element-2578" data-mp="wjkslgfxov92" />بياناتك محمية ولا تُشارك مع الآخرين.</p><Button variant="primary" type="submit" disabled={isSubmitting} data-id="element-2579" data-mp="rjf6sgwci2yw">{isSubmitting ? 'جارٍ الإرسال…' : 'إرسال الرسالة'}</Button></div></form>;
}
function Field({
  label,
  placeholder,
  type = 'text',
  required = false,
  value,
  error,
  onChange
}: {
  label: string;
  placeholder: string;
  type?: string;
  required?: boolean;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  const id = `arabic-contact-${label}`;
  return <div data-id="element-2580" data-mp="58x15vpflknq"><label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-900" data-id="element-2581" data-mp="58x68a4agaul">{label}{required && <span className="text-forest-700" data-id="element-2582" data-mp="a8jes69692x2"> *</span>}</label><input id={id} type={type} placeholder={placeholder} value={value} onChange={event => onChange(event.target.value)} aria-invalid={Boolean(error)} className={`w-full rounded-xl border bg-ivory/40 px-4 py-3 text-[15px] transition focus:bg-white focus:outline-none ${error ? 'border-red-400 focus:border-red-500' : 'border-sage-200 focus:border-forest-700'}`} data-id="element-2583" data-mp="f24dy1mi42xu" />{error && <p className="mt-1.5 text-xs text-red-600" data-id="element-2584" data-mp="xzoy4vebmdc4">{error}</p>}</div>;
}
function InfoCard({
  icon: Icon,
  title,
  tone,
  children
}: {
  icon: BoxIcon;
  title: string;
  tone: string;
  children: React.ReactNode;
}) {
  return <article className={`rounded-2xl border p-6 ${tone}`} data-id="element-2585" data-mp="2zsd256ysh4sy"><span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-forest-700" data-id="element-2586" data-mp="2zt4wkegngwhn"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-2587" data-mp="2nh2jm0oevvi3" /></span><h2 className="mt-4 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2588" data-mp="2nfhr0glnqmmv">{title}</h2>{typeof children === 'string' ? <p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-2589" data-mp="2ifmxe5ahziy4">{children}</p> : children}</article>;
}